/* eslint-disable react/jsx-no-constructed-context-values */
import type { FC, ReactNode } from 'react'
import { createContext, useState, useContext } from 'react'

import { getDefaultCountryId } from '@skytech/utils'

import { useAppContext } from '@/app/index.context'
import { useProfile } from '@/hooks/user'

type SubType = 'business' | 'non-business' | 'new-number' | 'private'
type SimType = 'new' | 'existing' | 'eSim'

type NewSubscriptionContext = {
  subType: [SubType, (v: SubType) => void]
  simType: [SimType | undefined, (v: SimType | undefined) => void]
  simCity: [string | undefined, (v: string | undefined) => void]
  simZip: [string | undefined, (v: string | undefined) => void]
  simNumber: [string | undefined, (v: string | undefined) => void]
  eid: [string | undefined, (v: string | undefined) => void]
  authorizationDocumentId: [number | null, (v: number | null) => void]
  employeeNumber: [string | null, (v: string | null) => void]
  comment: [string, (v: string) => void]
  email: [string, (v: string) => void]
  name: [string, (v: string) => void]
  activationDate: [string, (v: string) => void]
  contactNumber: [string | null, (v: string | null) => void]
  contactNumberCountryId: [number, (v: number) => void]
  departmentId: [number | null, (v: number | null) => void]
  devicePolicyId: [number | null, (v: number | null) => void]
  telePolicyId: [number | null, (v: number | null) => void]
  authorizationDocumentFilename: [string, (v: string) => void]
  simAddress: [string | undefined, (v: string | undefined) => void]
  ecomPolicyIds: [number[] | null, (v: number[] | null) => void]
  companyId: [number | null, (v: number | null) => void]
  allowPrivateNumber: [boolean, (v: boolean) => void]
  allowSubscriptions: [boolean, (v: boolean) => void]
  allowEsim: [boolean, (v: boolean) => void]
}

const Context = createContext<NewSubscriptionContext>({
  subType: ['non-business', () => {}],
  simType: [undefined, () => {}],
  simCity: [undefined, () => {}],
  eid: [undefined, () => {}],
  name: ['', () => {}],
  simZip: [undefined, () => {}],
  simNumber: [undefined, () => {}],
  authorizationDocumentId: [null, () => {}],
  employeeNumber: [null, () => {}],
  comment: ['', () => {}],
  email: ['', () => {}],
  activationDate: ['', () => {}],
  simAddress: [undefined, () => {}],
  authorizationDocumentFilename: ['', () => {}],
  contactNumber: [null, () => {}],
  // Norway
  contactNumberCountryId: [47, () => {}],
  devicePolicyId: [null, () => {}],
  telePolicyId: [null, () => {}],
  departmentId: [null, () => {}],
  ecomPolicyIds: [null, () => {}],
  companyId: [null, () => {}],
  allowPrivateNumber: [false, () => {}],
  allowSubscriptions: [false, () => {}],
  allowEsim: [false, () => {}],
})

export const NewSubscriptionContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const simZip = useState<string | undefined>(undefined)
  const simNumber = useState<string | undefined>(undefined)
  const simCity = useState<string | undefined>(undefined)
  const eid = useState<string | undefined>(undefined)
  const authorizationDocumentId = useState<number | null>(null)
  const employeeNumber = useState<string | null>(null)
  const contactNumber = useState<string | null>(null)
  const { locale } = useAppContext()
  const profile = useProfile()
  const defaultContactNumberCountryId = getDefaultCountryId(locale)
  const contactNumberCountryId = useState<number>(
    profile?.countryId || defaultContactNumberCountryId
  )
  const devicePolicyId = useState<number | null>(null)
  const telePolicyId = useState<number | null>(null)
  const departmentId = useState<number | null>(null)
  const ecomPolicyIds = useState<number[] | null>([])
  const comment = useState<string>('')
  const name = useState<string>('')
  const email = useState<string>('')
  const activationDate = useState<string>('')
  const simAddress = useState<string | undefined>(undefined)
  const authorizationDocumentFilename = useState<string>('')
  const subType = useState<SubType>('non-business')
  const simType = useState<SimType>()
  const companyId = useState<number | null>(null)
  const allowPrivateNumber = useState<boolean>(false)
  const allowSubscriptions = useState<boolean>(false)
  const allowEsim = useState<boolean>(false)

  const value: NewSubscriptionContext = {
    subType,
    simType,
    simZip: [simType[0] === 'new' ? simZip[0] : undefined, simZip[1]],
    simNumber: [simType[0] === 'existing' ? simNumber[0] : undefined, simNumber[1]],
    authorizationDocumentId,
    employeeNumber,
    comment,
    email,
    activationDate,
    simAddress: [simType[0] === 'new' ? simAddress[0] : undefined, simAddress[1]],
    authorizationDocumentFilename,
    contactNumber,
    contactNumberCountryId,
    devicePolicyId,
    telePolicyId,
    departmentId,
    ecomPolicyIds,
    simCity: [simType[0] === 'new' ? simCity[0] : undefined, simCity[1]],
    name,
    companyId,
    eid,
    allowPrivateNumber,
    allowSubscriptions,
    allowEsim,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useNewSubscriptionContext = () => useContext(Context)
